import { hasExplicitBillingVendor, resolveBillingVendor } from '@/services/billing/resolve-vendor';
import { BillingSystem } from '@/services/billing/types';
import type { BillingVendor } from '@/services/auth/types';
import { isEmptyString } from '@/utils/string';

type BillingVendorLabelAccount = {
  billing_vendor?: BillingVendor;
  ordway_id?: string | null;
};

function vendorName(vendor: BillingVendor): string {
  switch (vendor) {
    case BillingSystem.Ordway:
      return 'Ordway';
    case BillingSystem.Stripe:
      return 'Stripe';
    case BillingSystem.ModernStripe:
      // A distinct label so support can tell the two Stripe platforms apart; the legacy label stays a
      // bare "Stripe" so existing accounts read the same as before.
      return 'Stripe (modern)';
    case BillingSystem.Metronome:
      return 'Metronome';
    default: {
      const _exhaustive: never = vendor;
      return String(_exhaustive);
    }
  }
}

/**
 * The masquerade banner's "which billing system is this account on" tooltip.
 *
 * Reports the vendor the *backend* would act on, via `resolveBillingVendor`, rather than inferring it
 * from `ordway_id` presence. Telling support which billing system an account uses is this surface's
 * only job, and the old inference had just two outcomes, so Metronome accounts were reported as Stripe.
 *
 * When the vendor was inferred rather than stored, the label says so: for support, "the record says
 * Stripe" and "there is no record, so we assume Stripe" are different facts, and the second one is a
 * hint that the account's `billing_vendor` column was never set.
 *
 * The Ordway id is still shown when there is one, because support looks it up.
 */
export function billingVendorLabel(account: BillingVendorLabelAccount): string {
  const vendor = resolveBillingVendor(account);

  let label = `Billing managed by ${vendorName(vendor)}`;
  if (vendor === BillingSystem.Ordway && !isEmptyString(account.ordway_id)) {
    label += ` (${account.ordway_id})`;
  }
  if (!hasExplicitBillingVendor(account)) {
    label += ' (inferred, billing_vendor not set)';
  }
  return label;
}
